import { useNumberBooleanChoices } from 'hooks/useNumberBooleanChoices';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import {
  FilterAutocompleteChip,
  FilterTextChip,
} from 'shared/mui/Filter/FilterChipsToolbar/FilterChips';
import { AutocompleteInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';
import { Merchant } from 'types/embedded/merchant';

export const MerchantShowAccountsFiltersChips = () => {
  const translate = useTypedTranslate();
  const record = useRecordContext<Merchant>();
  const numberBooleanChoices = useNumberBooleanChoices();
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const { watch, setValue, resetField } = useFormContext();

  const providerWatched = watch('provider');

  return (
    <>
      <FilterTextChip label={translate('app.filter.name')} name="name">
        <TextInput
          helperText={false}
          label={translate('app.filter.name')}
          resettable
          source="name"
          variant="outlined"
        />
      </FilterTextChip>
      <FilterAutocompleteChip
        label={translate('app.filter.provider')}
        name="provider"
        onApply={() => {
          resetField('gateways', { keepDirty: true });
          setAppliedFilter('gateways', null);
        }}
        onDelete={() => {
          resetField('gateways', { keepDirty: true });
          setAppliedFilter('gateways', null);
        }}
        onReset={() => {
          setValue('gateways', appliedFilters?.['gateways']);
          setAppliedFilter('gateways', appliedFilters?.['gateways']);
        }}
        resource="providers"
      >
        <ReferenceInput
          filter={{ merchantAccounts: { merchants: record?.id } }}
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label={translate('app.filter.provider')}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      </FilterAutocompleteChip>
      <FilterAutocompleteChip
        label={translate('app.filter.gateway')}
        name="gateways"
        resource="gateways"
      >
        <ReferenceInput
          filter={{ provider: providerWatched }}
          perPage={100}
          reference="gateways"
          source="gateways"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label={translate('app.filter.gateway')}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      </FilterAutocompleteChip>
      <FilterAutocompleteChip
        label={translate('app.filter.currency')}
        name="currency"
        resource="currencies"
      >
        <ReferenceInput
          filter={{ active: true }}
          perPage={100}
          reference="currencies"
          source="currency"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            fullWidth
            helperText={false}
            label={translate('app.filter.currency')}
            optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            variant="outlined"
          />
        </ReferenceInput>
      </FilterAutocompleteChip>
      <FilterTextChip
        getName={(filterValue: string) =>
          numberBooleanChoices.find((value) => value.id === filterValue)?.label || ''
        }
        label={translate('app.filter.partners')}
        name="partner"
      >
        <AutocompleteInput
          choices={numberBooleanChoices}
          label={translate('app.filter.partners')}
          name="partner"
          optionText="label"
          variant="outlined"
        />
      </FilterTextChip>
    </>
  );
};
